import { Card, type CardPayload, themes } from "@evervault/react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { type ExtendedProperty } from "../../hooks/use-available-payment-methods";

interface EvervaultCardProps {
  fields: Record<"card_number" | "expiration_date", ExtendedProperty>;
}

export const EvervaultCard = (props: EvervaultCardProps) => {
  const { fields } = props;
  const { t } = useTranslation();
  const {
    setValue,
    formState: { errors, isSubmitted },
  } = useFormContext();
  const [evervaultShowsNumberError, setEvervaultShowsNumberError] =
    useState(false);
  const [evervaultShowsExpiryError, setEvervaultShowsExpiryError] =
    useState(false);

  const handleNumberChange = ({ card, errors }: CardPayload) => {
    setEvervaultShowsNumberError(Boolean(errors?.number));
    setValue("card_number", card.number ?? "", {
      shouldValidate: isSubmitted,
    });
  };

  const handleExpiryChange = ({ card: { expiry }, errors }: CardPayload) => {
    setEvervaultShowsExpiryError(Boolean(errors?.expiry));
    setValue(
      "expiration_date",
      expiry.month && expiry.year ? `${expiry.month}/${expiry.year}` : "",
      { shouldValidate: isSubmitted }
    );
  };

  const cardTheme = themes.clean({
    styles: {
      input: {
        boxShadow: "none",
        borderColor: "hsl(214.3 31.8% 91.4%)",
        height: "2.5rem",
        borderRadius: "6px",
        fontSize: "14px",
        color: "hsl(222.2 84% 4.9%)",
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
      },
      label: {
        fontSize: "16px",
        color: "hsl(222.2 84% 4.9%)",
        paddingBottom: "4px",
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
      },
      fieldset: {
        gap: "8px",
      },
    },
  });

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col gap-y-2">
        <div
          role="group"
          aria-label={fields.card_number.title || "Card Number"}
        >
          <Card
            translations={{
              number: {
                label: fields.card_number.title || "Card Number",
                errors: { invalid: t("invalid_card_number") },
              },
              expiry: {},
              cvc: {},
            }}
            theme={cardTheme}
            fields={["number"]}
            onChange={handleNumberChange}
          />
        </div>
        {errors.card_number && !evervaultShowsNumberError && (
          <p className="text-sm font-medium text-destructive">
            {errors.card_number.message as string}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-y-2">
        <div
          role="group"
          aria-label={fields.expiration_date.title || "Expiration Date"}
        >
          <Card
            translations={{
              number: {},
              expiry: {
                label: fields.expiration_date.title || "Expiration Date",
                errors: { invalid: t("invalid_expiration_date") },
              },
              cvc: {},
            }}
            theme={cardTheme}
            fields={["expiry"]}
            onChange={handleExpiryChange}
          />
        </div>
        {errors.expiration_date && !evervaultShowsExpiryError && (
          <p className="text-sm font-medium text-destructive">
            {errors.expiration_date.message as string}
          </p>
        )}
      </div>
    </div>
  );
};
